import React, { useEffect, useState, useRef } from "react";
import { Modal, Form, Input, Select, message, Alert, Tabs, Button, Tag, Space, Card, Row, Col, Segmented, Tooltip, Divider, Badge } from "antd";
import {
    RobotOutlined,
    FileTextOutlined,
    CheckCircleOutlined,
    CodeOutlined,
    EyeOutlined,
    SaveOutlined,
    ThunderboltOutlined,
    InfoCircleOutlined,
    CopyOutlined
} from "@ant-design/icons";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Editor from "@monaco-editor/react";
import { PutJutgePracticaIdDocumentId } from "../../../../dao/jutge/PutJutgePracticaIdDocumentId";
import { PostJutgePracticaIdDocument } from "../../../../dao/jutge/PostJutgePracticaIdDocument";
import { GenerateJunit } from "../../../../dao/jutge/GenerateJunit";
import { GenerateEnunciat } from "../../../../dao/jutge/GenerateEnunciat";

interface DocumentType {
    id: number;
    nom: string;
    descripcio?: string;
    tipo: string; // "enunciat" | "junit" | "es" | "codi"
    visible: number;
    enunciat?: string;
    test?: string;
}

interface EditDocumentModalProps {
    visible: boolean;
    document: DocumentType;
    onClose: (updated: boolean) => void;
    idPractica?: string;
    practicaNom?: string;
}

const EditDocumentModal: React.FC<EditDocumentModalProps> = ({ visible, document, onClose, idPractica, practicaNom }) => {
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const [editorContent, setEditorContent] = useState("");
    const [currentTipo, setCurrentTipo] = useState(document.tipo);
    const [showHtml, setShowHtml] = useState(true);
    const [activeTab, setActiveTab] = useState<string>("editor");
    const [generatedTests, setGeneratedTests] = useState<any[]>([]); // New state for AI results
    const [aiPrompt, setAiPrompt] = useState("");
    const [generatedEnunciat, setGeneratedEnunciat] = useState("");
    const previousClassNameRef = useRef("");

    const staticInstructionsEnunciado = `
Dado el enunciado proporcionado, formatea una práctica para los alumnos con las siguientes condiciones:
- Ha de ser en HTML, con los css inline.
- En caso de tener tablas, utiliza la etiqueta **pre** para que se vea correctamente.
- No te comas ninguna parte del enunciado, respeta todo lo que se proporciona para ello.
- Respecta y revisa las puntuaciones (si no suman 10 puntos revisalo y modificalo).
- NO des las soluciones, solamente el enunciado.

Además como respuesta se ha de obtener únicmaente una documento HTML con todos los tests segun especifica el prompt.
Asegurate de que siempre lo único que se genera es una clase HTML con los datos requeridos.`;


    const staticInstructions = `
Dado el enunciado y los tests proporcionados (si los hay) Genérame los tests unitarios para evaluar este ejercicio con las siguientes condiciones:
- No han de tener etiquetas de JUnit (tests, before, etc..)
- Ha de comprobar la estructura de los métodos respetando la firma, entrada y retorno de todos los métodos.
- Si tienen puntuación, se ha de respetar.
- Primero de todo ha de comprobar si el package y el nombre de la clase se han crado bien, sinó no se ejcuta nada más.
- Has de dar la nota final del ejercicio en el formato:
  System.out.println("==============================");
  System.out.println("TOTAL TESTS: " + TOTAL_TESTS);
  System.out.println("TESTS CORRECTOS: " + ok);
  System.out.printf("NOTA GLOBAL: %.2f", (ok * 100.0 / TOTAL_TESTS));
- Si el método no se ha encontrado, como obtenido se ha de poner: Metodo no encontrado.
- La nota final no puede ser superior a 100.
- calcula TOTAL_TESTS especificando cuantos tests hay.
- Haz que un metodo no puntue todos sus puntos si no tiene todos sus tests correctos, solo que falle uno es un 0 en ese test/metodo.
- Haz que todos los métodos tengan un mínimo de 2 tests para probar todas sus funcionalidades. No hay máximo.
- No uses carácteres especiales ni iconos.
- Los errores de estrucutra penalizan más que el resto de errores.
- Todos los métodos pesan lo mismo excepto que se especifique lo contrario.
- La salida de los prints ha de tener exactamente este formato:
  OK|metodo|entrada|esperado|obtenido
  ERROR|metodo|entrada|esperado|obtenido
- Es **MUY IMPORTANTE** que cada tests ocupe únicamente una línea.
- ES **MUY MUY IMPORTANTE** que cumplas los requisitos del enunciado en todos y cada uno de los tests, no te salgas del enunciado en cuanto a comprobar funcionalidades.
- Evalua todos los métodos con múltimes pruebas para no dejar ningun trozo de código sin comprobar.

Además como respuesta se ha de obtener únicmaente una clase .java con todos los tests segun especifica el prompt y para evaluar el enunciado que también se ha pasado dentro de los Docs y la entrada salida y los otros tests unitarios si los hay.
Al final, después de pulsar el botón de Generar test con ia se ha de generar una clase java ejecutable con los tests requeridos.
Asegurate de que siempre lo único que se genera es una clase Java con los tests requeridos.`;


    // Inicializar editor y campos
    useEffect(() => {
        const rawContent = document.enunciat || "";
        setEditorContent(rawContent);
        setCurrentTipo(document.tipo);

        // Si el contenido ya tiene etiquetas HTML como <table>, <div style=, etc., por defecto mostrar Monaco Editor
        if (rawContent.includes("<table") || rawContent.includes("<div") || rawContent.includes("<pre")) {
            setShowHtml(true);
        }

        let initialName = document.nom;

        if (!initialName) {
            if (document.tipo === "junit" || document.tipo === "codi") initialName = ".java";
            else if (document.tipo === "enunciat") initialName = "Enunciat";
            else if (document.tipo === "es") initialName = "es";
        }

        form.setFieldsValue({
            id: document.id,
            nom: initialName,
            descripcio: document.id === 0 && practicaNom ? practicaNom : document.descripcio,
            tipo: document.tipo,
        });
        previousClassNameRef.current = "";
    }, [document, form, practicaNom]);

    // Cambiar tipo dinámicamente
    const handleTipoChange = (value: string) => {
        setCurrentTipo(value);
        setEditorContent("");

        let name = "";
        if (value === "enunciat") name = "Enunciat";
        else if (value === "es") name = "es";

        form.setFieldsValue({ tipo: value, nom: name });
        previousClassNameRef.current = "";
    };

    // Validación dinámica del nombre
    const validateName = (_: any, value: string) => {
        if ((currentTipo === "junit" || currentTipo === "codi") && value && !value.endsWith(".java")) {
            return Promise.reject(new Error("El nombre del archivo debe terminar en .java"));
        }
        return Promise.resolve();
    };

    // Autocompletar nombre de clase Java y añadir .java
    useEffect(() => {
        if (currentTipo === "junit" || currentTipo === "codi") {
            const match = editorContent.match(/class\s+([A-Za-z_][A-Za-z0-9_]*)/);
            if (match) {
                let className = match[1];
                if (!className.endsWith(".java")) className += ".java";
                const currentName = form.getFieldValue("nom");
                if (!currentName || currentName === previousClassNameRef.current) {
                    form.setFieldsValue({ nom: className });
                    previousClassNameRef.current = className;
                }
            }
        }
    }, [editorContent, currentTipo, form]);

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            setSaving(true);

            let finalName = values.nom;
            if (currentTipo === "enunciat") finalName = "Enunciat";
            else if (currentTipo === "es") finalName = "es";
            else if ((currentTipo === "junit" || currentTipo === "codi") && finalName && !finalName.endsWith(".java")) {
                finalName += ".java";
            }

            let docId = Number(document?.id);
            if (!docId && values.id) {
                docId = Number(values.id);
            }

            const updatedDoc: DocumentType = {
                id: docId || 0,
                nom: finalName,
                descripcio: values.descripcio,
                tipo: values.tipo,
                visible: document.visible !== undefined ? document.visible : 1,
                enunciat: editorContent,
            };

            if (docId > 0) {
                await PutJutgePracticaIdDocumentId(docId, updatedDoc);
                message.success("Document actualitzat correctament");
            } else {
                if (!idPractica) {
                    message.error("Error: Falta ID de la pràctica");
                    setSaving(false);
                    return;
                }
                await PostJutgePracticaIdDocument(idPractica, updatedDoc);
                message.success("Document creat correctament");
            }
            onClose(true);
        } catch (err) {
            console.error(err);
            message.error("Error guardant el document");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            open={visible}
            title={
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingRight: 24 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <FileTextOutlined style={{ color: "#1890ff", fontSize: 20 }} />
                        <span style={{ fontSize: "1.15rem", fontWeight: 600 }}>
                            {document.id ? `Editar Document: ${document.nom}` : "Crear Nou Document / Test"}
                        </span>
                        {document.id ? <Tag color="blue">ID {document.id}</Tag> : <Tag color="green">Nou</Tag>}
                    </div>
                    {practicaNom && <Tag color="purple" style={{ marginRight: 8 }}>Pràctica: {practicaNom}</Tag>}
                </div>
            }
            onCancel={() => onClose(false)}
            onOk={handleSave}
            okText="Desar Canvis"
            cancelText="Cancel·lar"
            okButtonProps={{ icon: <SaveOutlined />, style: { borderRadius: 6 } }}
            confirmLoading={saving}
            width="85%"
            style={{ top: 20 }}
            centered
        >
            <Form layout="vertical" form={form} style={{ marginTop: 8 }}>
                <Form.Item name="id" hidden>
                    <Input />
                </Form.Item>
                {/* Meta details header card */}
                <Card
                    size="small"
                    style={{
                        marginBottom: 16,
                        borderRadius: 10,
                        background: "#fafafa",
                        border: "1px solid #f0f0f0"
                    }}
                >
                    <Row gutter={16} align="middle">
                        <Col xs={24} sm={8}>
                            <Form.Item
                                label={<span style={{ fontWeight: 600 }}>Tipus de Document</span>}
                                name="tipo"
                                rules={[{ required: true, message: "Selecciona el tipus" }]}
                                style={{ marginBottom: 0 }}
                            >
                                <Select
                                    size="middle"
                                    onChange={handleTipoChange}
                                    options={[
                                        { value: "enunciat", label: "📄 Enunciat (HTML / Text)" },
                                        { value: "junit", label: "🧪 JUnit Test (Java)" },
                                        { value: "es", label: "📥 Entrada / Sortida (Markdown)" },
                                        { value: "codi", label: "💻 Codi Font (Java / Plantilla)" },
                                    ]}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={10}>
                            <Form.Item
                                label={<span style={{ fontWeight: 600 }}>Nom del Fitxer</span>}
                                name="nom"
                                rules={[{ required: true, message: "Introdueix el nom del document" }, { validator: validateName }]}
                                style={{ marginBottom: 0 }}
                            >
                                <Input
                                    placeholder="Ex: Exercici1Test.java"
                                    disabled={currentTipo === "enunciat" || currentTipo === "es"}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={6}>
                            <Form.Item
                                label={<span style={{ fontWeight: 600 }}>Descripció Breu</span>}
                                name="descripcio"
                                style={{ marginBottom: 0 }}
                            >
                                <Input placeholder="Descripció opcional..." />
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>

                {/* AI Assistant Card for JUnit */}
                {currentTipo === "junit" && (
                    <Card
                        size="small"
                        title={
                            <span style={{ color: "#722ed1", fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                                <RobotOutlined /> Assistent IA: Generador de Tests JUnit
                            </span>
                        }
                        extra={
                            <Button
                                type="primary"
                                icon={<ThunderboltOutlined />}
                                loading={saving}
                                style={{ background: "#722ed1", borderColor: "#722ed1", borderRadius: 6 }}
                                onClick={async () => {
                                    if (!idPractica) {
                                        message.error("Error: No ID Practica");
                                        return;
                                    }
                                    setSaving(true);
                                    try {
                                        const fullPrompt = staticInstructions + "\n\n" + (editorContent || "");
                                        const result = await GenerateJunit(idPractica, fullPrompt);
                                        if (Array.isArray(result)) {
                                            setGeneratedTests(result);
                                            message.success(`Generats ${result.length} esborranys de tests. Tria el que vulguis.`);
                                        } else {
                                            message.warning("No s'ha rebut una llista de tests.");
                                        }
                                    } catch (e) {
                                        console.error(e);
                                        message.error("Error generant tests amb IA");
                                    } finally {
                                        setSaving(false);
                                    }
                                }}
                            >
                                Generar Tests amb IA
                            </Button>
                        }
                        style={{
                            marginBottom: 16,
                            borderRadius: 10,
                            borderColor: "#d3adf7",
                            background: "linear-gradient(to right, #faf5ff, #ffffff)"
                        }}
                    >
                        <Input.TextArea
                            rows={2}
                            placeholder="Afegeix instruccions adicionals per a la IA (ex: 'Puntua cada mètode amb 2 punts, prova casos extrems...')"
                            value={aiPrompt}
                            onChange={(e) => setAiPrompt(e.target.value)}
                            style={{ borderRadius: 6 }}
                        />

                        {generatedTests.length > 0 && (
                            <div style={{ marginTop: 14 }}>
                                <Divider style={{ margin: "10px 0" }} />
                                <span style={{ fontWeight: 600, fontSize: 13, color: "#595959" }}>
                                    ✨ Esborranys generats per la IA (Prem 'Carregar' per aplicar al codi):
                                </span>
                                <Tabs
                                    type="card"
                                    style={{ marginTop: 8 }}
                                    items={generatedTests.map((doc, idx) => ({
                                        key: String(idx),
                                        label: doc.nom || `Opció ${idx + 1}`,
                                        children: (
                                            <div style={{ background: "#f9f9f9", padding: 12, borderRadius: 8, border: "1px solid #eee" }}>
                                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                                                    <strong>{doc.nom}</strong>
                                                    <Button
                                                        type="primary"
                                                        size="small"
                                                        icon={<CopyOutlined />}
                                                        style={{ background: "#52c41a", borderColor: "#52c41a" }}
                                                        onClick={() => {
                                                            const code = doc.enunciat || "";
                                                            setEditorContent(code);
                                                            const match = code.match(/class\s+([a-zA-Z0-9_]+)/);
                                                            if (match && match[1]) {
                                                                form.setFieldsValue({ nom: match[1] + ".java" });
                                                            } else {
                                                                form.setFieldsValue({ nom: "TestGenerated.java" });
                                                            }
                                                            message.info("Codi carregat a l'editor principal.");
                                                        }}
                                                    >
                                                        Carregar i Editar Codi
                                                    </Button>
                                                </div>
                                                <Editor
                                                    height="220px"
                                                    defaultLanguage="java"
                                                    value={doc.enunciat || ""}
                                                    theme="vs-light"
                                                    options={{ readOnly: true, minimap: { enabled: false }, fontSize: 12 }}
                                                />
                                            </div>
                                        )
                                    }))}
                                />
                            </div>
                        )}
                    </Card>
                )}

                {/* AI Assistant Card for Enunciat */}
                {currentTipo === "enunciat" && (
                    <Card
                        size="small"
                        title={
                            <span style={{ color: "#1890ff", fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                                <RobotOutlined /> Assistent IA: Formatador d'Enunciat
                            </span>
                        }
                        extra={
                            <Button
                                type="primary"
                                icon={<ThunderboltOutlined />}
                                loading={saving}
                                style={{ borderRadius: 6 }}
                                onClick={async () => {
                                    setSaving(true);
                                    try {
                                        const fullPrompt = staticInstructionsEnunciado + "\n\n" + (aiPrompt || "");
                                        const result = await GenerateEnunciat(fullPrompt);
                                        if (result) {
                                            setGeneratedEnunciat(result);
                                            message.success("Enunciat generat correctament en format HTML.");
                                        } else {
                                            message.warning("No s'ha rebut contingut.");
                                        }
                                    } catch (e) {
                                        console.error(e);
                                        message.error("Error generant l'enunciat");
                                    } finally {
                                        setSaving(false);
                                    }
                                }}
                            >
                                Generar Enunciat amb IA
                            </Button>
                        }
                        style={{
                            marginBottom: 16,
                            borderRadius: 10,
                            borderColor: "#91caff",
                            background: "linear-gradient(to right, #f0f5ff, #ffffff)"
                        }}
                    >
                        <Input.TextArea
                            rows={2}
                            placeholder="Enganxa el text pla de l'enunciat o instruccions per a donar-li format HTML amb CSS inline..."
                            value={aiPrompt}
                            onChange={(e) => setAiPrompt(e.target.value)}
                            style={{ borderRadius: 6 }}
                        />

                        {generatedEnunciat && (
                            <div style={{ marginTop: 12, borderTop: "1px solid #e8e8e8", paddingTop: 10 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                                    <span style={{ fontWeight: 600 }}>Vista Prèvia de l'Enunciat Generat:</span>
                                    <Button
                                        type="primary"
                                        size="small"
                                        style={{ background: "#52c41a", borderColor: "#52c41a" }}
                                        onClick={() => {
                                            setEditorContent(generatedEnunciat);
                                            setGeneratedEnunciat("");
                                            message.success("Enunciat copiat a l'editor.");
                                        }}
                                    >
                                        👇 Acceptar i Copiar a l'Editor
                                    </Button>
                                </div>
                                <div
                                    style={{
                                        border: "1px solid #d9d9d9",
                                        padding: 12,
                                        maxHeight: 180,
                                        overflow: "auto",
                                        borderRadius: 6,
                                        background: "#fff"
                                    }}
                                    dangerouslySetInnerHTML={{ __html: generatedEnunciat }}
                                />
                            </div>
                        )}
                    </Card>
                )}

                {/* Main Content Editor Section */}
                <Card
                    bodyStyle={{ padding: 12 }}
                    style={{ borderRadius: 10, border: "1px solid #e8e8e8" }}
                    title={
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ fontSize: 14, fontWeight: 600 }}>
                                {currentTipo === "enunciat" ? "Contingut de l'Enunciat" : `Editor de Codi (${currentTipo.toUpperCase()})`}
                            </span>
                            {currentTipo === "enunciat" && (
                                <Segmented
                                    size="small"
                                    value={showHtml ? "html" : "visual"}
                                    onChange={(val) => setShowHtml(val === "html")}
                                    options={[
                                        { label: "Codi HTML", value: "html", icon: <CodeOutlined /> },
                                        { label: "Vista Prèvia", value: "visual", icon: <EyeOutlined /> }
                                    ]}
                                />
                            )}
                        </div>
                    }
                >
                    {currentTipo === "enunciat" ? (
                        <div>
                            {showHtml ? (
                                <Editor
                                    height="400px"
                                    defaultLanguage="html"
                                    value={editorContent}
                                    theme="vs-light"
                                    onChange={(value) => setEditorContent(value || "")}
                                    options={{
                                        minimap: { enabled: false },
                                        fontSize: 13,
                                        wordWrap: "on",
                                        lineNumbers: "on"
                                    }}
                                />
                            ) : (
                                <div
                                    className="enunciat-container"
                                    style={{
                                        minHeight: 350,
                                        maxHeight: 500,
                                        overflowY: "auto",
                                        padding: "16px 20px",
                                        border: "1px solid #d9d9d9",
                                        borderRadius: 6,
                                        background: "#fff"
                                    }}
                                    dangerouslySetInnerHTML={{ __html: editorContent }}
                                />
                            )}
                        </div>
                    ) : (
                        <Editor
                            height="50vh"
                            defaultLanguage={currentTipo === "codi" ? "javascript" : "java"}
                            value={editorContent || "// Introdueix el codi aquí"}
                            theme="vs-light"
                            onChange={(value) => setEditorContent(value || "")}
                            options={{
                                minimap: { enabled: false },
                                fontSize: 13,
                                lineNumbers: "on",
                                scrollBeyondLastLine: false,
                                automaticLayout: true
                            }}
                        />
                    )}
                </Card>
            </Form>
        </Modal>
    );
};

export default EditDocumentModal;
